/**
 * ExhibitViewSettings — the exhibit-corner settings popover.
 *
 * View settings only (chart variant, and any other subtract-only catalog
 * sections the card passes). Cuts/filters never belong here; those stay in
 * the pill row. The writer/spec may pick the default view and omit toggles —
 * this helper renders only the sections it is given.
 */
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Settings } from 'lucide-react'

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/utils'

export interface ExhibitViewSettingOption {
  id: string
  label: string
}

export interface ExhibitViewSettingsSection {
  id: string
  label: string
  options: Array<ExhibitViewSettingOption>
  selected: string
  onSelect: (id: string) => void
}

interface ExhibitViewSettingsProps {
  sections: Array<ExhibitViewSettingsSection>
  /** Trigger testid — chart-type-only cards keep `qs-compare-segments-trigger`. */
  triggerTestId: string
  revealOnHover?: boolean
  className?: string
}

export function ExhibitViewSettings(props: ExhibitViewSettingsProps) {
  const { sections, triggerTestId, revealOnHover = false, className } = props

  const { t } = useTranslation('app')
  const [open, setOpen] = useState(false)
  const radioName = useId()

  const visibleSections = sections.filter((section) => section.options.length > 1)
  if (visibleSections.length === 0) {
    return null
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={triggerTestId}
          aria-label={t('quickStudyChrome.viewSettings')}
          title={t('quickStudyChrome.viewSettings')}
          className={cn(
            'relative inline-flex h-7 w-7 items-center justify-center rounded-md text-twin-chat-fg-muted',
            'transition-colors hover:bg-twin-chat-bubble/40 hover:text-twin-chat-fg',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-twin-chat-send/60',
            open && 'text-twin-chat-fg',
            revealOnHover &&
              'opacity-0 transition-opacity group-hover/plotcard:opacity-100 focus-visible:opacity-100',
            revealOnHover && open && 'opacity-100',
            className,
          )}
        >
          <Settings className="h-4 w-4" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="max-h-80 w-64 space-y-3 overflow-y-auto border-twin-chat-divider bg-twin-chat-surface p-3 text-twin-chat-fg"
        data-testid="qs-view-settings-menu"
      >
        {visibleSections.map((section) => {
          const isChartType = section.id === 'plot-variant'
          return (
            <div
              key={section.id}
              className="space-y-1"
              data-testid={isChartType ? 'qs-chart-type-section' : `qs-view-settings-section-${section.id}`}
            >
              <Text
                as="p"
                size="xs"
                weight="medium"
                className="leading-none text-twin-chat-fg-muted"
              >
                {section.label}
              </Text>
              {section.options.map((opt) => (
                <label
                  key={opt.id}
                  className="flex cursor-pointer items-center gap-2 rounded-md px-1.5 py-1 text-sm transition-colors hover:bg-twin-chat-bubble/30"
                >
                  <input
                    type="radio"
                    name={`${radioName}-${section.id}`}
                    checked={section.selected === opt.id}
                    onChange={() => section.onSelect(opt.id)}
                    className="h-3.5 w-3.5 accent-current"
                    data-testid={
                      isChartType
                        ? `qs-chart-type-option-${opt.id}`
                        : `qs-view-settings-option-${section.id}-${opt.id}`
                    }
                  />
                  <span className="min-w-0 flex-1 truncate" title={opt.label}>
                    {opt.label}
                  </span>
                </label>
              ))}
            </div>
          )
        })}
      </PopoverContent>
    </Popover>
  )
}
